// V94.15 : agrégateur d'alertes/actions à faire pour Anissa.
// Parcourt les clients + consultations + feedbacks et retourne une liste
// d'actions prioritaires triées par urgence.
// V94.23 : ajout des alertes pack steps (Bilan S4/S8/S12 dus selon planning).

import { getClients, getNutritionConsultations } from '../store';
import { getNextPendingStep } from './packSystem';

const DAY = 1000 * 60 * 60 * 24;

/**
 * Calcule les alertes pour un client donne. Retourne un array d'objets :
 * { level: 'danger' | 'warning' | 'info' | 'success',
 *   iconType: 'bell' | 'doc' | 'clock' | 'calendar' | 'warning'
 *             (identifiant sémantique — le rendu choisit l'icône SVG, zéro emoji),
 *   label: phrase courte,
 *   clientId,
 *   prenom,
 *   priority: number (plus grand = plus urgent) }
 */
export function computeClientAlerts(client) {
  const alerts = [];
  const consultations = getNutritionConsultations(client.id);
  const prenom = client.prenom || client.form?.prenom || 'Sans nom';

  // 2026-07-19 (retour Benoit : « ça ne sert à rien ») — les relances
  // automatiques d'ancienneté ont été retirées : « aucune consultation
  // depuis création », « 2 mois depuis dernière conso » et « pas de news
  // depuis N mois ». Elles matchaient toutes les vieilles fiches et les
  // clients de test, noyant le panneau sous du bruit permanent. La
  // détection d'inactivité RÉELLE vit désormais dans l'espace Suivi
  // (silences de ressentis > 7 j, donnée vivante de l'app cliente).
  // Ne restent ici que les ÉCHÉANCES : bilans S4/S8/S12 dus ou en retard.

  if (consultations.length === 0) return alerts;

  // V94.23 : alerte pack step (Bilan S4/S8/S12 selon le planning du pack client)
  // Plus precis et generique que l ancien check S4 brut, supporte tous les packs.
  try {
    const nextStep = getNextPendingStep(client);
    if (nextStep && nextStep.type === 'review') {
      const dueDate = new Date(nextStep.dueDate);
      const daysUntilDue = Math.floor((dueDate.getTime() - Date.now()) / DAY);

      if (nextStep.isLate) {
        // Etape passee non realisee
        const daysLate = -daysUntilDue;
        alerts.push({
          level: 'danger',
          iconType: 'clock',
          label: `${prenom} : ${nextStep.label} EN RETARD (${daysLate}j)`,
          clientId: client.id,
          prenom,
          priority: 250 + daysLate,
        });
      } else if (daysUntilDue <= 0) {
        // Du aujourd hui
        alerts.push({
          level: 'warning',
          iconType: 'calendar',
          label: `${prenom} : ${nextStep.label} dû aujourd'hui`,
          clientId: client.id,
          prenom,
          priority: 180,
        });
      } else if (daysUntilDue <= 3) {
        // Du dans <= 3 jours
        alerts.push({
          level: 'warning',
          iconType: 'bell',
          label: `${prenom} : ${nextStep.label} dans ${daysUntilDue}j`,
          clientId: client.id,
          prenom,
          priority: 150,
        });
      } else if (daysUntilDue <= 7) {
        // Du dans la semaine
        alerts.push({
          level: 'info',
          iconType: 'calendar',
          label: `${prenom} : ${nextStep.label} dans ${daysUntilDue}j`,
          clientId: client.id,
          prenom,
          priority: 100,
        });
      }
    }
  } catch {
    // Pack system non configure pour ce client : on ignore
  }

  return alerts;
}

/**
 * Aggrege les alertes de tous les clients, triees par priorite descendante.
 * @param {number} limit - nombre max d'alertes a retourner (top N)
 */
export function getAllClientAlerts(limit = 8) {
  const clients = getClients();
  const all = [];
  for (const c of clients) {
    const alerts = computeClientAlerts(c);
    all.push(...alerts);
  }
  all.sort((a, b) => b.priority - a.priority);
  return all.slice(0, limit);
}
